import { useState } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { Icon } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type { UpdateState, UpdateStatus } from "../../ipc/types";

const TONE: Record<UpdateStatus, "success" | "warning" | "danger" | "brand" | "neutral"> = {
  idle: "neutral", checking: "brand", available: "brand", downloading: "brand",
  downloaded: "success", none: "success", error: "danger", disabled: "neutral",
};
const LABEL: Record<UpdateStatus, string> = {
  idle: "En espera", checking: "Buscando…", available: "Disponible", downloading: "Descargando…",
  downloaded: "Lista para instalar", none: "Al día", error: "Error", disabled: "No configurada",
};

/**
 * Panel de actualizaciones (item 10). Muestra la versión actual, permite buscar actualizaciones y,
 * cuando hay una descargada, reiniciar para instalarla. El auto-update es opt-in (requiere que el
 * instalador se haya construido con un servidor de updates); si no, se informa con claridad.
 */
export function UpdatesPanel(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<UpdateState>((c) => c.getUpdateStatus(), []);
  const [busy, setBusy] = useState(false);

  const check = async (): Promise<void> => { setBusy(true); await client.checkForUpdates(); setBusy(false); reload(); };
  const install = async (): Promise<void> => { setBusy(true); await client.installUpdate(); setBusy(false); };

  const u = state.status === "ready" ? state.data : null;

  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="bolt" size={18} /> Actualizaciones</span>}
        subtitle="Mantén FABIAN-MELI al día. Las nuevas versiones se descargan solas cuando hay un servidor configurado."
        action={u ? <Badge tone={TONE[u.status]} dot>{LABEL[u.status]}</Badge> : null}
      />
      <CardBody>
        {state.status === "loading" ? <LoadingState label="Consultando…" /> :
         state.status === "error" ? <ErrorState message={state.error.message} onRetry={reload} /> :
         u ? (
          <>
            <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>Versión instalada: <b className="strong">v{u.version}</b></p>
            {u.detail ? <p className="muted" style={{ fontSize: 13.5 }}>{u.detail}</p> : null}
            <div className="pd-actions">
              {u.status === "downloaded" ? (
                <Button size="sm" icon="check" disabled={busy} onClick={install}>{busy ? "Reiniciando…" : "Reiniciar e instalar"}</Button>
              ) : (
                <Button variant="secondary" size="sm" icon="pulse" disabled={busy} onClick={check}>{busy ? "Buscando…" : "Buscar actualizaciones"}</Button>
              )}
            </div>
            {!u.configured ? (
              <p className="faint" style={{ fontSize: 12.5, marginTop: "var(--sp-3)" }}>La actualización automática requiere que el instalador se haya construido con un servidor de updates (ver build/UPDATES.md). Mientras tanto, actualiza ejecutando el instalador nuevo.</p>
            ) : null}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}
